import { useSelector } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import InfoIcon from "@Icons/InfoIcon";
import { AppState } from "@Store/index";
import { CommunityData } from "../types";
import { resolveCopy, renderBoldText } from "../utils";
import NeighbourhoodCard from "./NeighbourhoodCard";
import CARD_ICONS from "./cardAssets";
import styles from "../neighbourhoodSection.module.scss";

type CommunityTabProps = {
  data: CommunityData;
};

const KEY_PREFIX = "propertypage.components.neighbourhood.community";

const CommunityTab = ({ data }: CommunityTabProps) => {
  const { t } = useTranslationHook();

  // Card footer: the first entry of the "Universities" tab. Absent → the footer
  // strip and its divider are not rendered (P1D-5482).
  const nearestUniversity = useSelector((state: AppState) => {
    const tabs = state.propertyPageData?.nearbyLocations;
    if (!Array.isArray(tabs)) return null;
    const universityTab = tabs.find((tab: any) => tab?.name === "Universities");
    const first = universityTab?.items?.[0];
    if (!first?.name) return null;
    return { name: String(first.name), duration: first.duration ? String(first.duration) : "" };
  });

  const source = resolveCopy(t, data.sourceKey);
  const sourceLabel = resolveCopy(t, "propertypage.components.neighbourhood.source.label");
  const noiseStatement = data.noiseLevel
    ? resolveCopy(t, `${KEY_PREFIX}.banner.noise.${data.noiseLevel}`)
    : undefined;

  const cardStat =
    data.mainStat && data.statKind
      ? resolveCopy(t, `${KEY_PREFIX}.card.stat.${data.statKind}`, { percent: data.mainStat })
      : data.mainStat;
  const cardSubtext = data.statKind
    ? resolveCopy(t, `${KEY_PREFIX}.card.subtext.${data.statKind}`)
    : undefined;

  // Rows whose copy resolves to nothing are dropped along with their divider
  // (P1D-5482): a missing data point leaves no gap and no stray rule.
  const renderedFacts = data.facts
    .map((fact) => ({ fact, statement: resolveCopy(t, fact.key, fact.params) }))
    .filter(({ statement }) => !!statement);

  return (
    <div className={styles.contentArea}>
      <NeighbourhoodCard
        image={data.heroImage}
        icon={CARD_ICONS.community}
        label={resolveCopy(t, `${KEY_PREFIX}.card.label`)}
        stat={cardStat}
        subtext={cardSubtext}
        alt="Community neighbourhood view"
        footer={
          nearestUniversity ? (
            // Stacked: the label sits on its own line, the name below it,
            // truncated on that line while the duration stays put.
            <span className={styles.cardFooterStacked}>
              <span>{resolveCopy(t, `${KEY_PREFIX}.footer.nearestUniversity`)}</span>
              <span className={styles.cardFooterValueGroup}>
                <span className={styles.cardFooterName}>{nearestUniversity.name}</span>
                {nearestUniversity.duration && (
                  <span className={styles.cardFooterMeta}>
                    &middot; {nearestUniversity.duration}
                  </span>
                )}
              </span>
            </span>
          ) : undefined
        }
      />
      <div className={styles.detailsSection}>
        <div>
          {renderedFacts.map(({ fact, statement }, index) => (
            <div key={fact.key}>
              <div
                className={formatClassNames(styles.tableRow, index === 0 && styles.tableRowFirst)}
              >
                <span className={styles.tableText}>{renderBoldText(statement)}</span>
              </div>
              {index < renderedFacts.length - 1 && <div className={styles.tableDivider} />}
            </div>
          ))}

          {noiseStatement && (
            <div className={styles.noiseBanner}>
              <span className={styles.noiseBannerText}>{renderBoldText(noiseStatement)}</span>
            </div>
          )}
        </div>
        <p className={styles.sourceText}>
          <span className={styles.sourceIcon} aria-hidden="true">
            <InfoIcon width="14" height="14" fill="#6b7280" />
          </span>
          {sourceLabel} : {source}
        </p>
      </div>
    </div>
  );
};

export default wrapperHOC(CommunityTab, {
  componentName: "CommunityTab",
  showForChina: false,
  showForWhiteLable: false,
});
